import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, MapPin, Heart, Clock, Banknote, ShieldCheck, Phone, Mail, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";

const URG_LABEL = { urgent: "Urgent", "1-semaine": "Sous 1 semaine", "1-mois": "Sous 1 mois", flexible: "Flexible" };
const URG_COLOR = { urgent: "bg-destructive/10 text-destructive", "1-semaine": "bg-brand/10 text-brand", "1-mois": "bg-pro/10 text-pro", flexible: "bg-slate-200 text-slate-600" };

export default function LeadCard({ lead, onUnlocked, onLikeToggle, mode = "pro" }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [liked, setLiked] = useState(lead.is_liked);
  const [likes, setLikes] = useState(lead.likes);

  const handleLike = async (e) => {
    e?.stopPropagation();
    try {
      const { data } = await api.post(`/leads/${lead.id}/like`);
      setLiked(data.liked);
      setLikes((n) => n + (data.liked ? 1 : -1));
      onLikeToggle?.(lead.id, data.liked);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleUnlock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/unlock`);
      toast.success("Demande débloquée !");
      setOpen(false);
      onUnlocked?.(data.lead);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      toast.error(msg);
      if (err.response?.status === 402) {
        // Suggest tokens or sub
      }
    } finally {
      setBusy(false);
    }
  };

  const isPro = mode === "pro";
  const locked = isPro && !lead.is_unlocked;

  const openDetail = () => {
    if (locked) setOpen(true);
    else if (isPro) nav(`/pro/lead/${lead.id}`);
  };

  return (
    <>
      <article
        onClick={openDetail}
        className="relative bg-white rounded-2xl border border-border p-4 tap-scale shadow-sm cursor-pointer"
        data-testid={`lead-card-${lead.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${URG_COLOR[lead.urgency]}`}>
                {URG_LABEL[lead.urgency]}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-bone text-slate2">
                {lead.trade_name}
              </span>
            </div>
            <h3 className="font-heading font-bold text-base text-ink leading-snug truncate">{lead.title}</h3>
            <div className="flex items-center gap-3 text-xs text-slate2 mt-1.5">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {lead.area ? `${lead.area}, ` : ""}{lead.city}{lead.distance_km != null && ` · ${lead.distance_km} km`}</span>
              <span className="flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> {lead.budget_min}–{lead.budget_max}€</span>
            </div>
          </div>
        </div>

        <div className={`mt-3 ${locked ? "lead-blur" : ""}`}>
          <p className="text-sm text-slate2 leading-relaxed line-clamp-2">{lead.description}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate2">— {lead.owner_name}</span>
            <span className="text-xs text-slate2 flex items-center gap-1"><Clock className="w-3 h-3" /> nouveau</span>
          </div>
        </div>

        {locked && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            className="absolute inset-x-3 bottom-3 top-[88px] bg-gradient-to-t from-white/95 via-white/85 to-white/60 rounded-xl flex flex-col items-center justify-end pb-3 cursor-pointer"
            data-testid={`unlock-lead-${lead.id}`}
          >
            <div className="bg-ink text-white rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-2 shadow-lg">
              <Lock className="w-3.5 h-3.5" /> Voir la demande ({lead.unlock_price}€)
            </div>
            <span className="text-[11px] text-slate2 mt-2">
              {typeof lead.unlocked_count === "number"
                ? `${lead.unlocked_count}/${lead.unlock_slots || 3} artisans ont accès`
                : "3 artisans max contactés"}
            </span>
          </button>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 text-sm font-medium tap-scale ${liked ? "text-brand" : "text-slate2"}`}
            data-testid={`like-lead-${lead.id}`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-brand" : ""}`} /> {likes}
          </button>
          {!locked && !isPro && lead.is_unlocked && (
            <span className="text-xs text-forest flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Votre demande</span>
          )}
          {!locked && isPro && (
            <span className="text-xs font-medium text-forest">Débloqué ✓</span>
          )}
        </div>

        {isPro && !locked && (lead.owner_phone || lead.owner_email) && (
          <div className="mt-3 rounded-xl bg-forest/5 border border-forest/20 p-3 space-y-1.5" data-testid={`lead-contact-${lead.id}`}>
            <div className="text-[10px] uppercase tracking-widest text-forest font-bold">Coordonnées du client</div>
            {lead.owner_phone && (
              <a href={`tel:${lead.owner_phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-sm text-ink" data-testid={`lead-phone-${lead.id}`}>
                <Phone className="w-4 h-4 text-forest" /> {lead.owner_phone}
              </a>
            )}
            {lead.owner_email && (
              <a href={`mailto:${lead.owner_email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-sm text-ink break-all" data-testid={`lead-email-${lead.id}`}>
                <Mail className="w-4 h-4 text-forest" /> {lead.owner_email}
              </a>
            )}
            {lead.owner_id && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nav(`/pro/lead/${lead.id}`); }}
                className="w-full mt-2 bg-forest text-white rounded-full py-2 text-sm font-semibold flex items-center justify-center gap-2 tap-scale"
                data-testid={`lead-contact-btn-${lead.id}`}
              >
                <MessageCircle className="w-4 h-4" /> Voir la demande & répondre
              </button>
            )}
          </div>
        )}
      </article>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent data-testid="unlock-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-heading text-2xl">Débloquer cette demande</DrawerTitle>
            <DrawerDescription>
              Lisez les coordonnées du client et répondez directement pour décrocher ce chantier local.
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-2 space-y-3">
            <div className="rounded-2xl bg-bone p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate2">Pay-per-lead</div>
                  <div className="font-heading font-bold text-2xl text-ink">{lead.unlock_price}€</div>
                </div>
                <Button onClick={handleUnlock} disabled={busy} className="rounded-full bg-pro hover:bg-pro-hover" data-testid="confirm-unlock-btn">
                  {busy ? "Déblocage…" : "Débloquer"}
                </Button>
              </div>
              <p className="text-[11px] text-slate2 mt-2">Lead exclusif : 3 artisans maximum contactés.</p>
            </div>
            <a href="/pro/profile" className="block rounded-2xl border border-brand bg-brand-light p-4 tap-scale">
              <div className="text-xs uppercase tracking-wider text-brand font-semibold">Premium</div>
              <div className="font-heading font-bold text-ink">Abonnement 9,99€/mois</div>
              <p className="text-xs text-slate2 mt-1">Accès illimité à toutes les demandes + IA Pro + Priorité sur les leads.</p>
            </a>
          </div>
          <DrawerFooter>
            <DrawerClose asChild><Button variant="outline" className="rounded-full" data-testid="cancel-unlock-btn">Annuler</Button></DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
